/*
Filtering model for the example gallery.

Semantics are standard faceted search: **OR within a facet, AND across facets**.
Selecting Healthcare + Retail widens (either industry); adding Pickup/Dropoff then
narrows to PD examples within those industries. The previous behaviour AND-ed
every selection, so picking two tags almost always returned nothing.

Option counts are computed against the *other* facets' selections (and the search
term), so the number beside "Healthcare (6)" is what you'd actually get if you
clicked it. Zero-count options are disabled, which makes an empty result set
unreachable by clicking.
*/
import { getModelTypeMeta } from "store/functions/exampleCatalog.js";

/** "field-service" -> "Field service" */
const titleize = (v) =>
  String(v).replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const asList = (v) => (v == null || v === "" ? [] : [String(v)]);

/* Facet definitions. `valuesOf` returns the facet values an example carries —
 * an empty array means "not categorized", so the example is hidden whenever
 * that facet is filtered but still shows when it isn't. */
export const FACETS = [
  {
    // An example carries one PRIMARY `industry` — the label its card shows —
    // and may carry additional `secondary_industries`, so a scenario that
    // genuinely belongs to two verticals (special-needs school transport is
    // education AND healthcare) appears under both filters without the card
    // having to pick a side. Union, deduped; a backend that sends no
    // secondaries behaves exactly as before.
    key: "industry",
    label: "Industry",
    valuesOf: (ex) => {
      const secondary = Array.isArray(ex.secondary_industries)
        ? ex.secondary_industries.filter(Boolean).map(String)
        : [];
      return [...new Set([...asList(ex.industry), ...secondary])];
    },
    format: titleize,
  },
  {
    key: "model",
    label: "Model",
    valuesOf: (ex) => asList(ex.model_type),
    format: (v) => getModelTypeMeta(v).verbose || titleize(v),
  },
  {
    key: "scale",
    label: "Scale",
    valuesOf: (ex) => asList(ex.scale),
    format: titleize,
  },
  {
    key: "country",
    label: "Country",
    valuesOf: (ex) => asList(ex.geography?.country),
    format: (v) => v,
  },
  {
    // Backed by constraints_tags (a clean, closed set) rather than the legacy
    // `tags` field, which mixes constraints with industry values.
    key: "constraints",
    label: "Constraints",
    valuesOf: (ex) => (Array.isArray(ex.constraints_tags) ? ex.constraints_tags.filter(Boolean) : []),
    format: titleize,
  },
];

/** The facet a card's tag chips toggle. */
export const CONSTRAINTS_FACET_KEY = "constraints";

/** Chips to show on a card — the clean constraint set, falling back to legacy tags. */
export const cardTagsOf = (example) => {
  const c = example?.constraints_tags;
  if (Array.isArray(c) && c.length) return c.filter(Boolean);
  return (example?.tags || []).filter(Boolean);
};

/* Free-text search covers the fields people actually type: the name, the story,
 * and the place. Geography is deliberately searchable rather than a facet for
 * city/region — there are nearly as many cities as examples, so a dropdown of
 * them would be a wall, but "Tokyo" or "Germany" should still find its example. */
const haystack = (ex) =>
  [
    ex.title,
    ex.scenario_name,
    ex.brief_description,
    ex.industry,
    ex.geography?.city,
    ex.geography?.region,
    ex.geography?.country,
    ...(ex.tags || []),
    ...(ex.constraints_tags || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export function matchesSearch(example, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return haystack(example).includes(q);
}

/** True when the example satisfies one facet's selections (OR within). */
function matchesFacet(example, facet, selected) {
  if (!selected || selected.length === 0) return true;
  const values = facet.valuesOf(example);
  return values.some((v) => selected.includes(v));
}

/**
 * Apply the full filter set.
 * @param {Array} examples
 * @param {Object} selections - { [facetKey]: string[] }
 * @param {string} query
 * @param {string} [exceptKey] - skip this facet (used when counting its options)
 */
export function filterExamples(examples, selections, query, exceptKey) {
  return (examples || []).filter((ex) => {
    if (!matchesSearch(ex, query)) return false;
    return FACETS.every((f) =>
      f.key === exceptKey ? true : matchesFacet(ex, f, selections[f.key])
    );
  });
}

/**
 * Options for one facet, with the count each would yield if selected.
 * Counted against every *other* facet + search, which is what makes the numbers
 * honest. Sorted by count desc, then label — so the useful filters surface first
 * in a long list (e.g. 25 countries).
 */
export function facetOptions(examples, selections, query, facet) {
  const pool = filterExamples(examples, selections, query, facet.key);
  const counts = new Map();
  // Seed from the whole catalog so a selected-but-now-empty option still renders
  // (disabled) rather than vanishing while it's still checked.
  (examples || []).forEach((ex) => {
    facet.valuesOf(ex).forEach((v) => {
      if (!counts.has(v)) counts.set(v, 0);
    });
  });
  pool.forEach((ex) => {
    facet.valuesOf(ex).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  });

  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count, label: facet.format(value) }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export const countSelected = (selections) =>
  FACETS.reduce((n, f) => n + (selections[f.key]?.length || 0), 0);

export const emptySelections = () =>
  FACETS.reduce((acc, f) => ({ ...acc, [f.key]: [] }), {});

/* ---- URL sync -------------------------------------------------------------
 * Filter state lives in the query string so a filtered view is shareable and
 * linkable ("see all healthcare examples"), and so back/forward behave.
 */
export function selectionsToSearchParams(selections, query) {
  const params = new URLSearchParams();
  FACETS.forEach((f) => {
    const v = selections[f.key];
    if (v && v.length) params.set(f.key, v.join(","));
  });
  if (query && query.trim()) params.set("q", query.trim());
  return params;
}

export function searchParamsToSelections(search) {
  const params = new URLSearchParams(search || "");
  const selections = emptySelections();
  FACETS.forEach((f) => {
    const raw = params.get(f.key);
    if (raw) selections[f.key] = raw.split(",").filter(Boolean);
  });
  return { selections, query: params.get("q") || "" };
}
